// QuestionFlow — the continuous 23-question interview phase (build-order step 6).
//
// MOUNTED SOLELY from the consent_assembled stage in InterviewLanding (the stage
// reachable only via interview-assemble-pre-consent's 200). Do not mount elsewhere
// — that gate is the structural guarantee that no answer reaches answer-upload
// before the assembler has run. See docs/workstream-k-spec.md build-order step 6.
//
// FLOW (this file), per question: play the prompt, auto-record with silence
// detection (manual Done / Stop / Skip also terminate), upload to
// interview-answer-upload, then enter the tap-based REVIEWING state with four
// controls: Play back (replays the just-recorded local blob), Submit (advance),
// Re-record (same question, next attempt — disabled once attempt reaches
// MAX_ANSWER_ATTEMPTS), and Exit (hand off to the parent's resume flow). The
// older spoken confirmation step was removed in the v1 simplification
// (2026-06-02) — see docs/workstream-k-v1-simplification-spec.md.
//
// DEFERRED to later sub-steps (NOT built here): real skip semantics (patch the
// answer row to skipped=true) + the skip queue — "skip" currently drops the
// partial and writes an audio-less sentinel row; silent-answer handling;
// continued-story / mid-recording append.

import { useEffect, useRef, useState } from "react";
import { Loader2, Mic } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  acquireMicStream,
  MicPermissionDeniedError,
  recordWithSilenceDetection,
  type RecordResult,
} from "@/lib/audioRecorder";
import {
  CameraPermissionDeniedError,
  recordVideoWithParallelAudio,
  type VideoRecordResult,
} from "@/lib/videoRecorder";
import { uploadVideoToR2 } from "@/lib/videoUpload";
import { playPrompt, stopAllAudio } from "@/lib/gateAudio";
import { lldbg } from "@/lib/lldbg"; // [LLDBG] TEMP
import LldbgOverlay from "@/components/interview/LldbgOverlay"; // [LLDBG] TEMP
import { INTERVIEW_QUESTIONS } from "@/lib/interviewContent";

// Silence window that auto-stops the answer recording. Raised back to 6s
// (2026-06-04) from 3s: 3s cut subjects off mid-thought — too short for natural
// thinking pauses. The manual Done button (tier 2, below) is the deterministic
// terminator, so a long silence window is safe: it can't hang (Done always
// works; the 10-min max-duration cap is the hard backstop). History: 6s→3s on
// 2026-05-25 predated the Done button, when a long window risked hanging in noise.
const ANSWER_SILENCE_MS = 6_000;
// 20-min hard cap — enforced by recordWithSilenceDetection's RAF check AND its
// RAF-independent setTimeout backstop (fires even on a backgrounded tab). At the
// pinned 64 kbps below, a 20-min answer is ~9.2 MB, still well under
// interview-answer-upload's 25 MB pre-row ceiling (not reached until ~55 min), so
// a never-silent answer still stops well short of a 413.
const ANSWER_MAX_DURATION_MS = 1_200_000;
// Pin opus to speech-grade 64 kbps so recording size is deterministic
// (independent of the browser default) and the upload stays small.
const ANSWER_BITRATE_BPS = 64_000;
// Mirror ConsentGate's protection: if the analyser reads zeros (a degraded
// shared AudioContext), hasDetectedSpeech never becomes true, the trailing-
// silence timer never starts, and the answer records until the subject taps
// Done instead of truncating. The floor is larger than the gates' 800ms
// because answers are open-ended, not one-word.
const ANSWER_MIN_SPEECH_MS = 3000;
const ANSWER_REQUIRE_SPEECH_BEFORE_SILENCE = true;

// Frontend answer-attempt cap, mirroring interview-answer-upload's server cap of
// 3. Re-record is disabled in the reviewing state once attempt >= this, so the
// server's 4th-attempt 409 is never exercised.
const MAX_ANSWER_ATTEMPTS = 3;

// Video answer caps (Stage 4). 150MB hard client-side ceiling (the video cannot
// traverse the edge double-hop; checked BEFORE any network). 6-min max-duration
// backstop — no silence autostop, no countdown; Done is the terminator. At
// ~2.5Mbps video + 128kbps audio a 6-min take is ~113MB nominal (~75% of the
// 150MiB ceiling), leaving motion-overshoot margin below the reject.
const MAX_VIDEO_BYTES = 150 * 1024 * 1024;
const VIDEO_MAX_DURATION_MS = 360_000;
// Reused across legs for the 401/410 session-dead copy (kept identical to the
// audio leg's inline string).
const SESSION_EXPIRED_MSG =
  "Your session has expired. Please reopen the link in your email to continue.";

type Phase =
  | "playing_prompt"
  | "recording"
  | "stopped"
  | "uploading"
  | "reviewing"
  | "video_failed";

export type QuestionFlowProps = {
  resumeToken: string;
  audioContext: AudioContext;
  // Setup-time recording medium (Stage 4). "video" -> dual recorder + deferred
  // presign/PUT/audio/finalize chain; anything else -> the unchanged audio-only
  // path. InterviewLanding only mounts this once the medium is chosen.
  captureMedium: "video" | "audio";
  // Resume (piece 3): questions already dealt with. The flow starts at the first
  // question in NEITHER set (answered ∪ skipped). Default [] for a fresh start.
  answeredQuestionIds?: string[];
  skippedQuestionIds?: string[];
  onComplete: () => void;
  onError: (message: string) => void;
  onMicDenied: () => void;
  onExit: () => void;
};

const questionMp3 = (id: string) => `/audio/interview/v1/${id}.mp3`;

type UploadResult =
  | { kind: "ok" }
  | { kind: "fail"; status?: number; error: string };

const uploadAnswer = async (
  resumeToken: string,
  questionId: string,
  blob: Blob,
  mimeType: string,
  durationMs: number,
): Promise<UploadResult> => {
  const ext = mimeType.includes("mp4") || mimeType.includes("aac") ? "m4a" : "webm";
  const form = new FormData();
  form.append("question_id", questionId);
  form.append("audio", new File([blob], `${questionId}.${ext}`, { type: mimeType }));
  form.append("duration_ms", String(Math.round(durationMs)));
  form.append("skipped", "false");

  const { data, error } = await supabase.functions.invoke<{ ok: boolean }>(
    "interview-answer-upload",
    { body: form, headers: { Authorization: `Bearer ${resumeToken}` } },
  );

  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    let status: number | undefined;
    let message = "request failed";
    if (ctx && typeof ctx.status === "number") {
      status = ctx.status;
      try {
        const body = (await ctx.clone().json()) as { error?: string };
        message = body?.error ?? message;
      } catch {
        // leave default
      }
    }
    return { kind: "fail", status, error: message };
  }
  if (!data?.ok) return { kind: "fail", error: "malformed response" };
  return { kind: "ok" };
};

// Audio-less skip: POST interview-answer-upload with skipped="true" and NO audio.
// The function's skip branch writes a sentinel row (audio_url="", duration_ms=0,
// skipped=true) — no R2 object, no STT. Same error shape as uploadAnswer.
const uploadSkip = async (
  resumeToken: string,
  questionId: string,
): Promise<UploadResult> => {
  const form = new FormData();
  form.append("question_id", questionId);
  form.append("skipped", "true");

  const { data, error } = await supabase.functions.invoke<{ ok: boolean }>(
    "interview-answer-upload",
    { body: form, headers: { Authorization: `Bearer ${resumeToken}` } },
  );

  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    let status: number | undefined;
    let message = "request failed";
    if (ctx && typeof ctx.status === "number") {
      status = ctx.status;
      try {
        const body = (await ctx.clone().json()) as { error?: string };
        message = body?.error ?? message;
      } catch {
        // leave default
      }
    }
    return { kind: "fail", status, error: message };
  }
  if (!data?.ok) return { kind: "fail", error: "malformed response" };
  return { kind: "ok" };
};

// ----- Video deferred-chain helpers (Stage 4) -------------------------------
// Each mirrors uploadAnswer's invoke + error-extraction shape. String-literal
// `kind` discriminants (repo runs strict:false, so boolean discriminants don't
// narrow — string literals do). These are CLIENT-OWNED-attempt aware: the
// attempt N flows presign -> (echoed) video PUT -> audio upload -> finalize.

type PresignResult =
  | { kind: "ok"; url: string; key: string; attemptNumber: number }
  | { kind: "stale" }
  | { kind: "max_attempts" }
  | { kind: "session" }
  | { kind: "fail"; message: string };

const presignVideo = async (
  resumeToken: string,
  questionId: string,
  attemptNumber: number,
  contentType: string,
): Promise<PresignResult> => {
  const { data, error } = await supabase.functions.invoke<{
    url?: string;
    key?: string;
    attempt_number?: number;
  }>("interview-video-presign", {
    body: { question_id: questionId, attempt_number: attemptNumber, content_type: contentType },
    headers: { Authorization: `Bearer ${resumeToken}` },
  });
  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    let status: number | undefined;
    let body: { error?: string } | null = null;
    if (ctx && typeof ctx.status === "number") {
      status = ctx.status;
      try {
        body = (await ctx.clone().json()) as { error?: string };
      } catch {
        // leave null
      }
    }
    if (status === 401 || status === 410) return { kind: "session" };
    if (status === 409) {
      // Two distinct 409s by error string: stale -> auto-bump; max -> hard stop.
      if (body?.error === "stale attempt") return { kind: "stale" };
      if (body?.error === "max attempts reached") return { kind: "max_attempts" };
      return { kind: "fail", message: body?.error ?? "request failed" };
    }
    return { kind: "fail", message: body?.error ?? "request failed" };
  }
  if (!data?.url || typeof data.attempt_number !== "number") {
    return { kind: "fail", message: "malformed response" };
  }
  return { kind: "ok", url: data.url, key: data.key ?? "", attemptNumber: data.attempt_number };
};

type StepResult =
  | { kind: "ok" }
  | { kind: "session" }
  | { kind: "fail"; message: string };

// Parallel-audio upload for a video answer. The File MUST carry
// { type: audioContentType } so interview-answer-upload derives the audio ext
// from File.type identically to finalize's audio_content_type (key parity).
const uploadVideoAnswerAudio = async (
  resumeToken: string,
  questionId: string,
  attemptNumber: number,
  audioBlob: Blob,
  audioContentType: string,
  durationMs: number,
): Promise<StepResult> => {
  const form = new FormData();
  form.append("question_id", questionId);
  form.append("attempt_number", String(attemptNumber));
  form.append("audio", new File([audioBlob], `${questionId}.audio`, { type: audioContentType }));
  form.append("duration_ms", String(Math.round(durationMs)));
  form.append("skipped", "false");

  const { data, error } = await supabase.functions.invoke<{ ok: boolean }>(
    "interview-answer-upload",
    { body: form, headers: { Authorization: `Bearer ${resumeToken}` } },
  );
  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    let status: number | undefined;
    let message = "request failed";
    if (ctx && typeof ctx.status === "number") {
      status = ctx.status;
      try {
        const b = (await ctx.clone().json()) as { error?: string };
        message = b?.error ?? message;
      } catch {
        // leave default
      }
    }
    if (status === 401 || status === 410) return { kind: "session" };
    return { kind: "fail", message };
  }
  if (!data?.ok) return { kind: "fail", message: "malformed response" };
  return { kind: "ok" };
};

const finalizeVideo = async (
  resumeToken: string,
  questionId: string,
  attemptNumber: number,
  videoContentType: string,
  audioContentType: string,
  durationMs: number,
): Promise<StepResult> => {
  const { data, error } = await supabase.functions.invoke<{ ok: boolean }>(
    "interview-answer-finalize",
    {
      body: {
        question_id: questionId,
        attempt_number: attemptNumber,
        video_content_type: videoContentType,
        audio_content_type: audioContentType,
        duration_ms: durationMs,
      },
      headers: { Authorization: `Bearer ${resumeToken}` },
    },
  );
  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    let status: number | undefined;
    let message = "request failed";
    if (ctx && typeof ctx.status === "number") {
      status = ctx.status;
      try {
        const b = (await ctx.clone().json()) as { error?: string };
        message = b?.error ?? message;
      } catch {
        // leave default
      }
    }
    if (status === 401 || status === 410) return { kind: "session" };
    return { kind: "fail", message };
  }
  if (!data?.ok) return { kind: "fail", message: "malformed response" };
  return { kind: "ok" };
};

// Secondary, de-emphasized control available throughout the question phase so a
// subject can leave at any time (progress is saved per-answer; resume re-enters
// at the next unanswered question).
const ExitButton = ({ onExit }: { onExit: () => void }) => (
  <Button
    size="lg"
    variant="ghost"
    onClick={onExit}
    className="text-base text-muted-foreground hover:text-foreground"
  >
    Exit
  </Button>
);

const QuestionFlow = ({
  resumeToken,
  audioContext,
  captureMedium,
  answeredQuestionIds = [],
  skippedQuestionIds = [],
  onComplete,
  onError,
  onMicDenied,
  onExit,
}: QuestionFlowProps) => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>("playing_prompt");
  const [attempt, setAttempt] = useState(1);
  // Reviewing state: the just-recorded answer is held as an object URL so the
  // reviewing render can gate on it and the VIDEO leg can preview the blob. mimeType
  // drives the audio-vs-video element choice. Audio answers no longer play back on
  // this screen (removed — silent-first-tap iOS bug); Re-record is the recovery path.
  const [reviewMedia, setReviewMedia] = useState<{ url: string; mimeType: string } | null>(null);
  // True when the answer under review was cut off by the max-duration cap
  // (recorder stoppedReason === "max_duration"), NOT a manual Done. Drives the
  // truncation notice in the reviewing render. Cleared at each recording start so
  // a prior truncation never leaks into the next answer's review.
  const [wasTruncated, setWasTruncated] = useState(false);
  // Video-only (Stage 4): browser-direct PUT progress percent (null = not in a
  // PUT) and the inline failure card state that replaces the terminal onError
  // dead-end for chain failures. mode drives which actions the card offers.
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [videoFail, setVideoFail] = useState<
    { message: string; mode: "retry" | "rerecord" | "hardstop" } | null
  >(null);

  // Insulate the loop from parent re-renders (callback identities change every
  // render but the loop must not restart) — same pattern as ConsentGate.
  const cbRef = useRef({ onComplete, onError, onMicDenied, onExit });
  cbRef.current = { onComplete, onError, onMicDenied, onExit };

  // Holds the current answer recording's AbortController so the Done, Stop, and
  // Skip buttons can terminate it. Set just before the answer record() call,
  // cleared right after. answerActionRef carries the post-abort intent: "save"
  // (Done / silence / max-duration -> upload the partial), "discard" (Stop ->
  // drop the partial, no row, replay + re-record the same question), or "skip"
  // (Skip -> drop the partial, persist an audio-less sentinel skip row, advance).
  const answerStopRef = useRef<AbortController | null>(null);
  const answerActionRef = useRef<"save" | "discard" | "skip">("save");
  // ONE mic stream reused across the AUDIO answer recordings so iOS doesn't
  // re-prompt per question. Acquired lazily on the first audio record (one prompt)
  // and reused; re-acquired if iOS ends the track mid-interview (see
  // ensureLiveMicStream). Owned here — the recorder's ownsStream guard never stops
  // an injected stream — so the effect cleanup must release it. NOT used by the
  // video leg (its own combined video+audio stream) or FinalConsent.
  const answerMicStreamRef = useRef<MediaStream | null>(null);
  // Resolves the "stopped" idle gate when the subject taps Re-record.
  const restartResolveRef = useRef<(() => void) | null>(null);
  // Resolves the "reviewing" gate on the subject's Submit / Re-record tap.
  // SINGLE-SHOT: the button handlers null this BEFORE calling it, so a double-tap
  // cannot resolve twice and double-advance the loop — mirrors restartResolveRef.
  const reviewResolveRef = useRef<((action: "submit" | "re-record") => void) | null>(null);
  // Resolves the "video_failed" card on the subject's Try-again / Re-record tap.
  // SINGLE-SHOT like reviewResolveRef.
  const videoFailResolveRef = useRef<((action: "retry" | "re-record") => void) | null>(null);
  // Mirror of reviewMedia.url for imperative revocation from the unmount cleanup
  // (which closes over the effect, not render state).
  const reviewMediaRef = useRef<string | null>(null);
  // answered ∪ skipped = "dealt with". Held in a ref (like cbRef) so a changing
  // array identity from the parent never restarts the loop; read once at mount to
  // pick the resume start index.
  const dealtWithRef = useRef<Set<string>>(new Set());
  dealtWithRef.current = new Set([...answeredQuestionIds, ...skippedQuestionIds]);

  // Return a LIVE mic stream to reuse across audio answer recordings, acquiring
  // lazily (first record = one iOS prompt) and re-acquiring if the track died
  // (iOS suspend can end it mid-interview — never record silence into a dead
  // stream). Bare `true` constraints keep the clone-training raw default. Returns
  // null on acquire failure (denied); the caller then lets the recorder acquire
  // per-call as before, so a shared-stream miss never blocks the flow.
  const ensureLiveMicStream = async (): Promise<MediaStream | null> => {
    const current = answerMicStreamRef.current;
    if (current && current.getAudioTracks()[0]?.readyState === "live") {
      return current;
    }
    // Dead/absent stream: stop any stale tracks before re-acquiring.
    if (current) {
      current.getTracks().forEach((t) => t.stop());
      answerMicStreamRef.current = null;
    }
    try {
      const stream = await acquireMicStream(true);
      answerMicStreamRef.current = stream;
      return stream;
    } catch {
      // Denied/unavailable — leave the ref null; the recorder acquires per-call.
      answerMicStreamRef.current = null;
      return null;
    }
  };

  useEffect(() => {
    let alive = true;

    // runQuestion is self-recursive (advance / re-record). It's a FUNCTION
    // DECLARATION (not a const arrow) so it can reference itself regardless of
    // textual order — no temporal-dead-zone concern.
    async function runQuestion(i: number, currentAttempt: number): Promise<void> {
      if (i >= INTERVIEW_QUESTIONS.length) {
        cbRef.current.onComplete();
        return;
      }
      const q = INTERVIEW_QUESTIONS[i];
      setIndex(i);
      setAttempt(currentAttempt);

      try {
        // VIDEO (Stage 4): a fully separate leg (dual recorder + deferred upload
        // chain). The audio-only body below is UNTOUCHED and runs for every other
        // medium exactly as before.
        if (captureMedium === "video") {
          await runVideoLeg(i, currentAttempt, q);
          return;
        }

        // Prompt + record, looping on Stop: a "discard" intent drops the partial
        // and re-enters at the prompt (replay) for the SAME question/attempt — no
        // upload, no row, no attempt bump. The loop exits only when the recording
        // is kept ("save": Advance, silence, or max-duration).
        let answer: RecordResult | null = null;
        while (true) {
          setPhase("playing_prompt");
          const promptText = q.text;
          const promptMp3 = questionMp3(q.id);
          await playPrompt(promptText, promptMp3);
          if (!alive) return;

          setPhase("recording");
          answerActionRef.current = "save";
          setWasTruncated(false);
          const answerStop = new AbortController();
          answerStopRef.current = answerStop;
          // Reuse the one shared mic stream (no per-question iOS re-prompt). When
          // null (acquire denied), the recorder acquires its own stream per-call as
          // before — never block the flow. No audioConstraints: the shared stream
          // is already bare `true`, the clone-training raw default.
          const stream = await ensureLiveMicStream();
          if (!alive) {
            // Unmounted DURING the acquire: the effect cleanup already ran before
            // this resolved, so release the just-acquired stream here or it leaks
            // (hot mic after leaving the page).
            if (answerMicStreamRef.current) {
              answerMicStreamRef.current.getTracks().forEach((t) => t.stop());
              answerMicStreamRef.current = null;
            }
            return;
          }
          const rec = await recordWithSilenceDetection({
            maxDurationMs: ANSWER_MAX_DURATION_MS,
            silenceMs: ANSWER_SILENCE_MS,
            audioBitsPerSecond: ANSWER_BITRATE_BPS,
            audioContext,
            stopSignal: answerStop.signal,
            minSpeechMs: ANSWER_MIN_SPEECH_MS,
            requireSpeechBeforeSilence: ANSWER_REQUIRE_SPEECH_BEFORE_SILENCE,
            sharedStream: stream ?? undefined,
          });
          answerStopRef.current = null;
          if (!alive) return;

          // Stop: discard the partial (no upload, no row, attempt unchanged), wait
          // for the Re-record tap, then loop to replay the prompt and record the
          // same question/attempt from the top.
          if (answerActionRef.current === "discard") {
            setPhase("stopped");
            await waitForRestart();
            if (!alive) return;
            continue;
          }

          // Skip: discard the partial audio, persist an audio-less sentinel skip
          // row, and advance — no review step, no re-ask. A skipped question is
          // permanently dealt with (consume-token reports it in
          // skipped_question_ids; resume never resurfaces it).
          if (answerActionRef.current === "skip") {
            // Second resume point: the recorder teardown triggered by the abort is
            // itself an iOS audio-session change that can suspend the context AFTER
            // the tap, so the onClick resume above may have been a no-op. This runs
            // post-teardown but still before the network await. Fire-and-forget so
            // it overlaps the upload rather than adding latency.
            lldbg("skip:branch", { audioContextState: audioContext.state }); // [LLDBG] TEMP
            if (audioContext.state === "suspended") {
              void audioContext.resume().catch(() => {});
            }
            setPhase("uploading");
            const skipResult = await uploadSkip(resumeToken, q.id);
            if (!alive) return;
            if (skipResult.kind === "fail") {
              if (skipResult.status === 401 || skipResult.status === 410) {
                cbRef.current.onError(
                  "Your session has expired. Please reopen the link in your email to continue.",
                );
                return;
              }
              cbRef.current.onError(
                skipResult.error ?? "We couldn't skip that question. Please try again.",
              );
              return;
            }
            await runQuestion(i + 1, 1);
            return;
          }

          answer = rec;
          break;
        }

        setPhase("uploading");
        const result = await uploadAnswer(
          resumeToken,
          q.id,
          answer.blob,
          answer.mimeType,
          answer.durationMs,
        );
        if (!alive) return;

        if (result.kind === "fail") {
          if (result.status === 401 || result.status === 410) {
            cbRef.current.onError(
              "Your session has expired. Please reopen the link in your email to continue.",
            );
            return;
          }
          cbRef.current.onError(result.error ?? "We couldn't save that answer. Please try again.");
          return;
        }

        // Upload OK and is_current=true server-side. Enter the tap-based reviewing
        // state, then await the subject's Submit / Re-record decision. (Exit is
        // handled outside the loop via onExitClick — the saved row already stands.)
        // reviewMedia gates the reviewing render; the object URL is retained for
        // parity with the video leg and revoked in clearReviewMedia.
        const url = URL.createObjectURL(answer.blob);
        reviewMediaRef.current = url;
        setReviewMedia({ url, mimeType: answer.mimeType });
        setWasTruncated(answer.stoppedReason === "max_duration");
        lldbg("answer", { questionId: q.id, stoppedReason: answer.stoppedReason }); // [LLDBG] TEMP
        setPhase("reviewing");
        const decision = await waitForReview();
        if (!alive) return;
        clearReviewMedia();
        if (decision === "re-record") {
          await runQuestion(i, currentAttempt + 1);
        } else {
          await runQuestion(i + 1, 1);
        }
      } catch (err) {
        if (!alive) return;
        if (err instanceof MicPermissionDeniedError) {
          cbRef.current.onMicDenied();
          return;
        }
        // No answer is saved yet at this point, so an error is genuinely fatal
        // to this question — surface it (unchanged from sub-step 1).
        const msg = err instanceof Error ? err.message : "unknown error";
        cbRef.current.onError(`Recording failed: ${msg}`);
      }
    }

    // VIDEO leg (Stage 4). Mirrors the audio leg's loop shape — prompt, record
    // (Stop = discard + re-record same attempt; Skip = audio-less sentinel +
    // advance; Done / max-duration = keep) — but uses the dual recorder (no
    // silence autostop) and DEFERS all network to Submit. Client-owned attempt:
    // N = the loop's currentAttempt, carried through the whole chain.
    async function runVideoLeg(
      i: number,
      currentAttempt: number,
      q: { id: string; text: string },
    ): Promise<void> {
      let rec: VideoRecordResult | null = null;
      while (true) {
        setPhase("playing_prompt");
        await playPrompt(q.text, questionMp3(q.id));
        if (!alive) return;

        setPhase("recording");
        answerActionRef.current = "save";
        setWasTruncated(false);
        const answerStop = new AbortController();
        answerStopRef.current = answerStop;
        let r: VideoRecordResult;
        try {
          r = await recordVideoWithParallelAudio({
            maxDurationMs: VIDEO_MAX_DURATION_MS,
            stopSignal: answerStop.signal,
          });
        } catch (err) {
          answerStopRef.current = null;
          if (!alive) return;
          // Camera/mic denial routes to the existing mic-denied stage; other
          // recorder failures bubble to runQuestion's shared catch -> onError.
          if (err instanceof CameraPermissionDeniedError) {
            cbRef.current.onMicDenied();
            return;
          }
          throw err;
        }
        answerStopRef.current = null;
        if (!alive) return;

        // Read once via an `as`-cast so the literal comparisons below don't trip
        // the ref-narrowing TS2367 the audio leg carries as its tsc baseline (a
        // plain annotation still inherits the "save" flow-narrowing; the cast
        // forces the union as the expression type and defeats it).
        const action = answerActionRef.current as "save" | "discard" | "skip";
        if (action === "discard") {
          setPhase("stopped");
          await waitForRestart();
          if (!alive) return;
          continue;
        }
        if (action === "skip") {
          setPhase("uploading");
          setUploadProgress(null);
          const skipResult = await uploadSkip(resumeToken, q.id);
          if (!alive) return;
          if (skipResult.kind === "fail") {
            if (skipResult.status === 401 || skipResult.status === 410) {
              cbRef.current.onError(SESSION_EXPIRED_MSG);
              return;
            }
            cbRef.current.onError(
              skipResult.error ?? "We couldn't skip that question. Please try again.",
            );
            return;
          }
          await runQuestion(i + 1, 1);
          return;
        }
        rec = r;
        break;
      }

      // 150MB client ceiling BEFORE any network. Oversize -> re-record only.
      if (rec.videoBlob.size > MAX_VIDEO_BYTES) {
        setVideoFail({
          message:
            "That recording is too large to upload. A shorter answer — most are 1–3 minutes — will save reliably.",
          mode: "rerecord",
        });
        setPhase("video_failed");
        const d = await waitForVideoFail();
        setVideoFail(null);
        if (!alive) return;
        if (d === "re-record") {
          await runQuestion(i, currentAttempt + 1);
        }
        return;
      }

      // Reviewing: preview the LOCAL video blob (deferred — NO upload yet).
      const url = URL.createObjectURL(rec.videoBlob);
      reviewMediaRef.current = url;
      setReviewMedia({ url, mimeType: rec.videoContentType });
      setWasTruncated(rec.stoppedReason === "max_duration");
      setPhase("reviewing");
      const decision = await waitForReview();
      if (!alive) return;
      if (decision === "re-record") {
        clearReviewMedia();
        await runQuestion(i, currentAttempt + 1);
        return;
      }

      // Submit -> deferred chain. Inline retry/re-record card on failure replaces
      // the audio leg's terminal onError dead-end; session 401/410 still routes to
      // the existing session-expired handling. Card Retry bumps the attempt.
      let chainAttempt = currentAttempt;
      while (true) {
        setPhase("uploading");
        setUploadProgress(null);
        const result = await runVideoUploadChain(q.id, chainAttempt, rec);
        if (!alive) return;
        if (result.kind === "ok") {
          clearReviewMedia();
          await runQuestion(i + 1, 1);
          return;
        }
        if (result.kind === "session") {
          clearReviewMedia();
          cbRef.current.onError(SESSION_EXPIRED_MSG);
          return;
        }
        if (result.kind === "hardstop") {
          setVideoFail({ message: result.message, mode: "hardstop" });
          setPhase("video_failed");
          return;
        }
        setVideoFail({ message: result.message, mode: "retry" });
        setPhase("video_failed");
        const d = await waitForVideoFail();
        setVideoFail(null);
        if (!alive) return;
        if (d === "re-record") {
          clearReviewMedia();
          await runQuestion(i, currentAttempt + 1);
          return;
        }
        // Retry: re-run the chain at a FRESH attempt past the HIGHEST one this chain
        // reached (including any stale auto-bumps inside runVideoUploadChain), SAME
        // in-memory blobs. Resuming at result.lastAttempt + 1 (not a blind +1) avoids
        // colliding with attempts already burned, and keeps the visible "Try N" in sync.
        chainAttempt = result.lastAttempt + 1;
        setAttempt(chainAttempt);
      }
    }

    // ONE Submit's deferred chain: presign -> video PUT -> audio upload ->
    // finalize. Auto-bumps ONLY on presign "stale attempt" (pre-PUT, so clean),
    // bounded to 2 extra tries. Every other failure returns retryable for the
    // inline card; "max attempts reached" is a hard stop (no retry).
    async function runVideoUploadChain(
      questionId: string,
      startAttempt: number,
      rec: VideoRecordResult,
    ): Promise<
      | { kind: "ok" }
      | { kind: "session" }
      | { kind: "hardstop"; message: string }
      | { kind: "fail"; message: string; lastAttempt: number }
    > {
      let n = startAttempt;
      let staleBumps = 0;
      while (true) {
        const presign = await presignVideo(resumeToken, questionId, n, rec.videoContentType);
        if (presign.kind === "session") return { kind: "session" };
        if (presign.kind === "max_attempts") {
          return {
            kind: "hardstop",
            message:
              "You've used all your tries for this question. Tap Exit to finish later, or contact us for help.",
          };
        }
        if (presign.kind === "stale") {
          if (staleBumps >= 2) {
            return {
              kind: "fail",
              message: "We couldn't start this upload after several tries. Please try again.",
              lastAttempt: n,
            };
          }
          staleBumps += 1;
          n += 1;
          setAttempt(n);
          continue;
        }
        if (presign.kind === "fail") {
          return { kind: "fail", message: "We couldn't start the upload. Please try again.", lastAttempt: n };
        }
        const echoedN = presign.attemptNumber;

        // Browser-direct video PUT (progress + stall timeout). Any failure
        // (stall / abort / non-2xx) is retryable via the inline card.
        try {
          await uploadVideoToR2({
            blob: rec.videoBlob,
            url: presign.url,
            contentType: rec.videoContentType,
            onProgress: (loaded, total) => {
              setUploadProgress(total > 0 ? Math.round((loaded / total) * 100) : null);
            },
          });
        } catch {
          return { kind: "fail", message: "The video upload didn't finish. Please try again.", lastAttempt: echoedN };
        }
        setUploadProgress(100);

        const audioRes = await uploadVideoAnswerAudio(
          resumeToken,
          questionId,
          echoedN,
          rec.audioBlob,
          rec.audioContentType,
          rec.durationMs,
        );
        if (audioRes.kind === "session") return { kind: "session" };
        if (audioRes.kind === "fail") {
          return { kind: "fail", message: "We couldn't save the audio for this answer. Please try again.", lastAttempt: echoedN };
        }

        const finRes = await finalizeVideo(
          resumeToken,
          questionId,
          echoedN,
          rec.videoContentType,
          rec.audioContentType,
          rec.durationMs,
        );
        if (finRes.kind === "session") return { kind: "session" };
        if (finRes.kind === "fail") {
          return { kind: "fail", message: "We couldn't finish saving this answer. Please try again.", lastAttempt: echoedN };
        }

        return { kind: "ok" };
      }
    }

    // Resolves on the subject's Try-again / Re-record tap in the video_failed card.
    function waitForVideoFail(): Promise<"retry" | "re-record"> {
      return new Promise((resolve) => {
        videoFailResolveRef.current = resolve;
      });
    }

    // Resolves when the subject taps Re-record from the "stopped" idle state.
    function waitForRestart(): Promise<void> {
      return new Promise<void>((resolve) => {
        restartResolveRef.current = resolve;
      });
    }

    // Resolves on the subject's Submit / Re-record tap in the reviewing state.
    function waitForReview(): Promise<"submit" | "re-record"> {
      return new Promise((resolve) => {
        reviewResolveRef.current = resolve;
      });
    }

    // Revoke the review object URL and reset the reviewing UI. Called after the
    // subject's decision resolves (component still mounted).
    function clearReviewMedia() {
      if (reviewMediaRef.current) {
        URL.revokeObjectURL(reviewMediaRef.current);
        reviewMediaRef.current = null;
      }
      setReviewMedia(null);
    }

    // Resume: start at the first question in NEITHER answered NOR skipped
    // (answered ∪ skipped = dealt-with; a skipped question never resurfaces). If
    // every question is dealt with, startIndex === length -> onComplete.
    const dealtWith = dealtWithRef.current;
    let startIndex = INTERVIEW_QUESTIONS.findIndex((q) => !dealtWith.has(q.id));
    if (startIndex < 0) startIndex = INTERVIEW_QUESTIONS.length;
    void runQuestion(startIndex, 1);

    return () => {
      // Order matters: alive=false FIRST, so when the abort below resolves the
      // recorder, the loop's `if (!alive) return` fires and the partial is
      // dropped (never uploaded) — one finalize path, no second. Then stop any
      // prompt audio and release the mic.
      alive = false;
      stopAllAudio();
      answerStopRef.current?.abort();
      // Release the shared answer mic stream we own (the recorder's ownsStream
      // guard never stops an injected stream, so this is the only release point).
      if (answerMicStreamRef.current) {
        answerMicStreamRef.current.getTracks().forEach((t) => t.stop());
        answerMicStreamRef.current = null;
      }
      // Release any review object URL still held by the reviewing state.
      if (reviewMediaRef.current) URL.revokeObjectURL(reviewMediaRef.current);
    };
  }, [resumeToken, audioContext]);

  const total = INTERVIEW_QUESTIONS.length;
  const current = INTERVIEW_QUESTIONS[index];

  const isRecording = phase === "recording";
  const isUploading = phase === "uploading";

  const headline =
    phase === "recording"
      ? "Listening"
      : phase === "stopped"
      ? "Recording stopped"
      : phase === "uploading"
      ? "Saving your answer"
      : phase === "reviewing"
      ? "Review your answer"
      : phase === "video_failed"
      ? "We couldn't save that"
      : "Listen carefully";

  // Stop the in-flight prompt immediately, then hand off to the parent (-> the
  // "exited" stage -> unmount -> cleanup releases the mic via the abort
  // primitive; any partial answer is discarded, never uploaded).
  const onExitClick = () => {
    stopAllAudio();
    cbRef.current.onExit();
  };

  return (
    <div className="text-center" aria-live="polite">
      <div className="mb-6 text-sm uppercase tracking-wide text-muted-foreground">
        Question {index + 1} of {total}
        {attempt > 1 ? ` · Try ${attempt} of ${MAX_ANSWER_ATTEMPTS}` : ""}
      </div>

      {isRecording ? (
        <div className="mx-auto flex h-24 w-24 items-center justify-center">
          <span className="relative flex h-24 w-24">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive/40" />
            <span className="relative inline-flex h-24 w-24 items-center justify-center rounded-full bg-destructive">
              <Mic className="h-12 w-12 text-destructive-foreground" />
            </span>
          </span>
        </div>
      ) : isUploading ? (
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-muted">
          <Loader2 className="h-10 w-10 text-primary animate-spin" />
        </div>
      ) : (
        <div className="mx-auto flex h-24 w-24 items-center justify-center">
          <span className="relative flex h-24 w-24">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/30" />
            <span className="relative inline-flex h-24 w-24 rounded-full bg-primary/70" />
          </span>
        </div>
      )}

      <p className="mt-6 text-2xl font-display text-foreground">{headline}</p>
      {isUploading && uploadProgress !== null ? (
        <p className="mt-3 text-base text-muted-foreground">Uploading… {uploadProgress}%</p>
      ) : null}
      {phase === "playing_prompt" && current ? (
        <div className="mt-3">
          <p className="text-base text-muted-foreground max-w-md mx-auto">{current.text}</p>
          <div className="mt-4">
            <ExitButton onExit={onExitClick} />
          </div>
        </div>
      ) : null}
      {phase === "recording" ? (
        <div className="mt-2">
          <p className="text-base text-muted-foreground">
            {captureMedium === "video"
              ? "Tap Done when you've finished your answer. Stop re-records this question; Skip moves on without answering."
              : "Speak your answer — we'll move on when you finish, or tap Done. Stop re-records this question; Skip moves on without answering."}
          </p>
          {captureMedium === "video" ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Most meaningful answers are 1–3 minutes. A shorter answer is easier to share.
            </p>
          ) : null}
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              onClick={() => {
                answerActionRef.current = "save";
                answerStopRef.current?.abort();
              }}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Done
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                answerActionRef.current = "discard";
                answerStopRef.current?.abort();
              }}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Stop
            </Button>
            <Button
              size="lg"
              variant="ghost"
              onClick={() => {
                answerActionRef.current = "skip";
                // Skip's next prompt plays after uploadSkip's network round-trip, far
                // outside the tap's user-activation window, so gateAudio's gesture-less
                // resume misses and the prompt degrades to robotic SpeechSynthesis.
                // Resume here, inside the true synchronous gesture, in case the context
                // is already suspended. Fire-and-forget: never block the tap.
                lldbg("skip:tap", { audioContextState: audioContext.state }); // [LLDBG] TEMP
                if (audioContext.state === "suspended") {
                  void audioContext.resume().catch(() => {});
                }
                answerStopRef.current?.abort();
              }}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Skip
            </Button>
            <ExitButton onExit={onExitClick} />
          </div>
        </div>
      ) : null}
      {phase === "stopped" ? (
        <div className="mt-2">
          <p className="text-base text-muted-foreground">
            We discarded that recording. When you're ready, we'll play the question again so you can answer from the top.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              onClick={() => {
                const resume = restartResolveRef.current;
                restartResolveRef.current = null;
                resume?.();
              }}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Re-record
            </Button>
            <ExitButton onExit={onExitClick} />
          </div>
        </div>
      ) : null}
      {phase === "reviewing" && reviewMedia ? (
        <div className="mt-2">
          <p className="text-base text-muted-foreground">
            Submit to keep this answer, or re-record to try again.
          </p>
          {wasTruncated ? (
            <p className="mt-3 text-base text-muted-foreground max-w-md mx-auto">
              You've reached the maximum length for a single answer, so the recording stopped here. You can re-record this answer, or continue your thought in the next question.
            </p>
          ) : null}
          {reviewMedia.mimeType.startsWith("video") ? (
            // VIDEO answer: show the picture with the browser's native controls
            // (scrub/play/replay). playsInline = iOS must not force fullscreen. The
            // AUDIO answer has no review element: audio playback was removed (the
            // silent-first-tap iOS record-mode bug), so Re-record is the recovery path.
            <video
              src={reviewMedia.url}
              controls
              playsInline
              className="mx-auto mt-4 w-full max-w-md aspect-video rounded-xl bg-black object-contain"
            />
          ) : null}
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              onClick={() => {
                const resolve = reviewResolveRef.current;
                reviewResolveRef.current = null;
                resolve?.("submit");
              }}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Submit
            </Button>
            <div className="flex flex-col items-center gap-1">
              <Button
                size="lg"
                variant="outline"
                disabled={attempt >= MAX_ANSWER_ATTEMPTS}
                onClick={() => {
                  const resolve = reviewResolveRef.current;
                  reviewResolveRef.current = null;
                  resolve?.("re-record");
                }}
                className="text-xl px-10 py-7 h-auto rounded-2xl"
              >
                Re-record
              </Button>
              {attempt >= MAX_ANSWER_ATTEMPTS ? (
                <span className="text-xs text-muted-foreground">Last try — Submit when ready</span>
              ) : null}
            </div>
            <ExitButton onExit={onExitClick} />
          </div>
        </div>
      ) : null}
      {phase === "video_failed" && videoFail ? (
        <div className="mt-2">
          <p className="text-base text-muted-foreground max-w-md mx-auto">{videoFail.message}</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center items-center">
            {videoFail.mode === "retry" ? (
              <Button
                size="lg"
                onClick={() => {
                  const resolve = videoFailResolveRef.current;
                  videoFailResolveRef.current = null;
                  resolve?.("retry");
                }}
                className="text-xl px-10 py-7 h-auto rounded-2xl"
              >
                Try again
              </Button>
            ) : null}
            {videoFail.mode !== "hardstop" ? (
              <Button
                size="lg"
                variant="outline"
                disabled={attempt >= MAX_ANSWER_ATTEMPTS}
                onClick={() => {
                  const resolve = videoFailResolveRef.current;
                  videoFailResolveRef.current = null;
                  resolve?.("re-record");
                }}
                className="text-xl px-10 py-7 h-auto rounded-2xl"
              >
                Re-record
              </Button>
            ) : null}
            <ExitButton onExit={onExitClick} />
          </div>
        </div>
      ) : null}
      <LldbgOverlay />{/* [LLDBG] TEMP — ?debug=1 gated on-screen probe readout */}
    </div>
  );
};

export default QuestionFlow;
